import React, { useRef, useEffect, useState } from "react";
import * as echarts from "echarts";
import {
  normalizeTimestampToMs,
  convertToArgentinaTime,
  formatAxisLabelEvolution,
} from "../../helpers/widgetsCharts";

const Chart1 = ({
  dataChart,
  unit,
  heightPx,
  periodColors,
  colorMode = "period",
  deviceNameByMac = {},
  baseColor,
  isWidget = false,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const rafId = useRef(null);
  const [viewMode, setViewMode] = useState("total");
  // Detectar si los datos tienen total_valor (scope TOTAL) o valores por fase (scope PHASE)
  const hasTotal = React.useMemo(() => {
    return dataChart?.some((device) => {
      const data = device?.data || [];
      return (
        Array.isArray(data) &&
        data.some(
          (row) => row?.total_valor !== undefined && row?.total_valor !== null,
        )
      );
    });
  }, [dataChart]);

  useEffect(() => {
    if (!chartRef.current || !dataChart?.length) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    const myChart = echarts.init(chartRef.current);
    chartInstance.current = myChart;

    const measureMapping = {
      corriente: ["R_corriente", "S_corriente", "T_corriente"],
      tension: ["R_tension", "S_tension", "T_tension"],
      potActiva: ["R_pActiva", "S_pActiva", "T_pActiva"],
      potReactiva: ["R_pReactiva", "S_pReactiva", "T_pReactiva"],
      eActiva: ["R_eActiva", "S_eActiva", "T_eActiva"],
      eReactiva: ["R_eReactiva", "S_eReactiva", "T_eReactiva"],
    };

    const defaultKeys = ["R_valor", "S_valor", "T_valor"];
    const totalKey = "total_valor";

    const phaseColorsSingleDevice = ["#2ecc71", "#3498db", "#f39c12"];
    const devicePalette = [
      "#2ecc71",
      "#3498db",
      "#f39c12",
      "#e74c3c",
      "#9b59b6",
      "#1abc9c",
    ];

    const getDeviceLabel = (device) => {
      const mac = device?.mac;
      return (mac && deviceNameByMac?.[mac]) || mac || "Dispositivo";
    };

    const merged = new Map();
    const notingPeriods = new Set();

    dataChart.forEach((device, deviceIndex) => {
      const mac = device.mac;
      const period = device.period;
      const deviceLabel = getDeviceLabel(device);
      if (period) notingPeriods.add(String(period));

      const measureTypes = Object.keys(device).filter((k) =>
        Object.keys(measureMapping).includes(k),
      );

      if (measureTypes.length > 0) {
        measureTypes.forEach((measureType) => {
          const measureDataRaw = device[measureType] || [];
          const onlyThisMac = measureDataRaw.filter((d) => d?.mac === mac);
          if (!onlyThisMac.length) return;

          const key = `${mac}||${period ?? ""}||${measureType}`;
          if (!merged.has(key)) {
            merged.set(key, {
              mac,
              period,
              measureType,
              deviceIndex,
              deviceLabel,
              rows: [],
            });
          }
          merged.get(key).rows.push(...onlyThisMac);
        });
      } else {
        const measureDataRaw = device.data || [];
        const onlyThisMac = measureDataRaw.filter((d) => d?.mac === mac);
        if (!onlyThisMac.length) return;

        const key = `${mac}||${period ?? ""}||data`;
        if (!merged.has(key)) {
          merged.set(key, {
            mac,
            period,
            measureType: "data",
            deviceIndex,
            deviceLabel,
            rows: [],
          });
        }
        merged.get(key).rows.push(...onlyThisMac);
      }
    });

    const allFormatted = [];
    for (const it of merged.values()) {
      const formatted = (it.rows || [])
        .map((d) => {
          const ts = normalizeTimestampToMs(d.timestamp);
          return { ...d, ts, label: convertToArgentinaTime(ts) };
        })
        .filter((d) => Number.isFinite(d.ts))
        .sort((a, b) => a.ts - b.ts);

      if (!formatted.length) continue;

      allFormatted.push({
        formatted,
        measureType: it.measureType,
        mac: it.mac,
        period: it.period,
        deviceIndex: it.deviceIndex,
        deviceLabel: it.deviceLabel,
      });
    }

    const periodsCount = notingPeriods.size;
    const isEvolutionCompact = periodsCount > 1;

    const GAP_SLOTS = 2;
    let xAxisData = null;
    let xKeyIndex = null;

    if (isEvolutionCompact) {
      xAxisData = [];
      xKeyIndex = new Map();

      const grouped = new Map();
      allFormatted.forEach((it) => {
        const p = it.period ? String(it.period) : "no-period";
        if (!grouped.has(p)) grouped.set(p, []);
        grouped.get(p).push(it);
      });

      const periods = Array.from(grouped.keys()).sort((a, b) => {
        const aMin = Math.min(
          ...grouped.get(a).flatMap((g) => g.formatted.map((d) => d.ts)),
        );
        const bMin = Math.min(
          ...grouped.get(b).flatMap((g) => g.formatted.map((d) => d.ts)),
        );
        return aMin - bMin;
      });

      periods.forEach((p, pIdx) => {
        const tsSet = new Set();
        grouped
          .get(p)
          .forEach((g) => g.formatted.forEach((d) => tsSet.add(d.ts)));
        const tsList = Array.from(tsSet).sort((a, b) => a - b);

        tsList.forEach((ts) => {
          const key = `${p}::${ts}`;
          if (!xKeyIndex.has(key)) {
            xKeyIndex.set(key, xAxisData.length);
            xAxisData.push(convertToArgentinaTime(ts));
          }
        });

        if (pIdx < periods.length - 1) {
          for (let i = 0; i < GAP_SLOTS; i++) xAxisData.push("");
        }
      });
    }

    const series = [];
    const legendData = new Set();
    const devicesCount = dataChart.length;

    // Determinar tamaño de símbolos basado en cantidad de datos
    const totalDataPoints = allFormatted.reduce(
      (sum, item) => sum + item.formatted.length,
      0,
    );
    const avgDataPoints = totalDataPoints / Math.max(allFormatted.length, 1);
    const symbolSize = avgDataPoints > 100 ? 0 : avgDataPoints > 50 ? 3 : 5;
    const lineWidth = avgDataPoints > 100 ? 1.5 : 2;

    allFormatted.forEach(
      ({ formatted, measureType, period, deviceIndex, deviceLabel }) => {
        // Si hay total_valor, usar ese. Si no, usar las fases
        const keys = hasTotal
          ? [totalKey]
          : measureMapping[measureType] || defaultKeys;

        const baseDeviceColor =
          devicePalette[deviceIndex % devicePalette.length];

        const phaseLabel = (k) => {
          if (k.startsWith("R_")) return "Fase R";
          if (k.startsWith("S_")) return "Fase S";
          if (k.startsWith("T_")) return "Fase T";
          if (k === totalKey) return "Total";
          return k;
        };

        const namePrefix = `${deviceLabel} - `;
        const periodSuffix = period ? ` - ${period}` : "";

        // Si tenemos total_valor, mostrar como total directamente (sin opción de phases)
        // Si no tenemos total_valor, permitir cambiar entre phases y total
        if (hasTotal) {
          // Modo TOTAL: mostrar directo total_valor
          let color =
            devicesCount > 1 ? baseDeviceColor : baseColor || "#000000";

          if (colorMode === "period" && periodColors && periodColors[period]) {
            color = periodColors[period];
          }

          let totalData;

          if (isEvolutionCompact) {
            const p = period ? String(period) : "no-period";
            const arr = new Array(xAxisData.length).fill(null);

            formatted.forEach((d) => {
              const idx = xKeyIndex.get(`${p}::${d.ts}`);
              if (idx !== undefined) arr[idx] = Number(d[totalKey]) ?? null;
            });

            totalData = arr;
          } else {
            totalData = formatted.map((d) => [
              d.ts,
              Number(d[totalKey]) ?? null,
            ]);
          }

          series.push({
            name: `${namePrefix}${phaseLabel(totalKey)}${periodSuffix}`,
            type: "line",
            data: totalData,
            smooth: true,
            connectNulls: true,
            symbol: symbolSize > 0 ? "circle" : "none",
            symbolSize: symbolSize,
            showAllSymbol: false,
            lineStyle: { color, width: lineWidth },
            itemStyle: {
              color: echarts.color.lift(color, 0.1),
              borderColor: echarts.color.lift(color, -0.35),
              borderWidth: 1,
            },
            emphasis: {
              itemStyle: {
                color: echarts.color.lift(color, 0.1),
                borderColor: echarts.color.lift(color, -0.25),
              },
              lineStyle: { width: lineWidth + 1 },
            },
            z: 5,
          });

          return;
        }

        // Modo PHASE: mostrar fases o total según viewMode
        if (viewMode === "phases") {
          keys.forEach((key, i) => {
            let color;
            if (devicesCount === 1) {
              color =
                phaseColorsSingleDevice[i % phaseColorsSingleDevice.length];
            } else {
              color = baseDeviceColor;
            }

            if (
              colorMode === "period" &&
              periodColors &&
              periodColors[period]
            ) {
              color = periodColors[period];
            }

            const seriesName = `${namePrefix}${phaseLabel(key)}${periodSuffix}`;
            legendData.add(seriesName);

            const data = isEvolutionCompact
              ? (() => {
                  const p = period ? String(period) : "no-period";
                  const arr = new Array(xAxisData.length).fill(null);
                  formatted.forEach((d) => {
                    const idx = xKeyIndex.get(`${p}::${d.ts}`);
                    if (idx !== undefined) arr[idx] = d[key] ?? null;
                  });
                  return arr;
                })()
              : formatted.map((d) => [d.ts, d[key] ?? null]);

            series.push({
              name: seriesName,
              type: "line",
              data,
              smooth: true,
              connectNulls: true,
              symbol: symbolSize > 0 ? "circle" : "none",
              symbolSize: symbolSize,
              showAllSymbol: false,
              lineStyle: { color, width: lineWidth },
              itemStyle: {
                color: echarts.color.lift(color, 0.1),
                borderColor: echarts.color.lift(color, -0.35),
                borderWidth: 1,
              },
              emphasis: {
                itemStyle: {
                  color: echarts.color.lift(color, 0.1),
                  borderColor: echarts.color.lift(color, -0.25),
                },
                lineStyle: { width: lineWidth + 1 },
              },
            });
          });

          return;
        }

        const totalName = `${namePrefix}Total${periodSuffix}`;
        legendData.add(totalName);

        const totalData = isEvolutionCompact
          ? (() => {
              const p = period ? String(period) : "no-period";
              const arr = new Array(xAxisData.length).fill(null);
              formatted.forEach((d) => {
                const idx = xKeyIndex.get(`${p}::${d.ts}`);
                if (idx === undefined) return;
                const sum =
                  keys.length > 1
                    ? keys.reduce((acc, k) => acc + (Number(d[k]) || 0), 0)
                    : (Number(d[keys[0]]) ?? null);
                arr[idx] = Number.isFinite(sum) ? sum : null;
              });
              return arr;
            })()
          : formatted.map((d) => {
              const sum =
                keys.length > 1
                  ? keys.reduce((acc, k) => acc + (Number(d[k]) || 0), 0)
                  : (Number(d[keys[0]]) ?? null);
              return [d.ts, Number.isFinite(sum) ? sum : null];
            });

        const totalColor =
          devicesCount > 1 ? baseDeviceColor : baseColor || baseDeviceColor;

        series.push({
          name: totalName,
          type: "line",
          data: totalData,
          smooth: true,
          connectNulls: true,
          symbol: symbolSize > 0 ? "circle" : "none",
          symbolSize: symbolSize,
          showAllSymbol: false,
          lineStyle: {
            color: totalColor,
            width: lineWidth,
          },
          itemStyle: {
            color: echarts.color.lift(totalColor, 0.1),
            borderColor: echarts.color.lift(totalColor, -0.35),
            borderWidth: 1,
          },
          emphasis: {
            itemStyle: {
              color: echarts.color.lift(totalColor, 0.1),
              borderColor: echarts.color.lift(totalColor, -0.25),
            },
            lineStyle: { width: lineWidth + 1 },
          },
          z: 5,
        });
      },
    );

    const showLegend =
      (viewMode === "phases" && legendData.size > 0) ||
      (viewMode === "total" && devicesCount > 1 && legendData.size > 0);
    const chartWidth = myChart.getWidth();
    const shouldRotate = chartWidth < 600;

    // Grid con espacios para toolbox (arriba) y legend (abajo)
    const gridConfig = {
      left: 30,
      right: 30,
      top: 50,
      bottom: isWidget
        ? 50
        : legendData.size
          ? 90
          : 80,
      containLabel: true,
      backgroundColor: "transparent",
    };

    // Configurar legend debajo del gráfico con altura limitada
    const legendConfig = showLegend
      ? {
          data: Array.from(legendData),
          type: "scroll",
          orient: "horizontal",
          left: "center",
          bottom: 5,
          textStyle: { fontSize: 11, color: "#666" },
          padding: [5, 10],
          itemGap: 15,
          maxHeight: 35,
          backgroundColor: "transparent",
          inactiveColor: "#bfbfbf",
          selectedMode: true,
        }
      : undefined;

    // Configurar toolbox arriba y centrado
    const toolboxConfig = {
      orient: "horizontal",
      left: "center",
      top: 8,
      feature: { dataZoom: { yAxisIndex: "none" }, restore: {} },
      itemSize: 18,
      itemGap: 12,
      tooltip: {
        show: true,
        backgroundColor: "rgba(0,0,0,0.85)",
        textStyle: { color: "#fff", fontSize: 11 },
        borderRadius: 4,
        padding: [6, 10],
      },
      iconStyle: {
        borderColor: "#999",
      },
    };

    const option = {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          lineStyle: { color: "#ddd", width: 1, type: "dashed" },
        },
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        borderColor: "#ddd",
        textStyle: { color: "#333", fontSize: 11 },
        padding: [8, 12],
        formatter: function (params) {
          const axisValue =
            params?.[0]?.axisValueLabel ?? params?.[0]?.axisValue ?? "";
          let tooltipText = `<div style="font-weight:600;margin-bottom:6px;color:#1a202c;">${axisValue}</div>`;

          params.forEach((item) => {
            const raw = Array.isArray(item.data) ? item.data[1] : item.data;
            const value =
              typeof raw === "number" ? raw.toFixed(2) : (raw ?? "-");
            tooltipText += `<div style="color:${item.color};margin:3px 0;">${item.marker} <span style="color:#555;">${item.seriesName}</span>: <strong style="color:${item.color};">${value}</strong> ${
              unit ?? ""
            }</div>`;
          });

          return tooltipText;
        },
        confine: true,
      },
      toolbox: toolboxConfig,
      dataZoom: isWidget
        ? []
        : [
            {
              type: "slider",
              height: 40,
              bottom: 36,
              fillerColor: "rgba(52,152,219,0.15)",
              borderColor: "#dcdcdc",
              handleSize: "120%",
            },
          ],
      legend: legendConfig,
      grid: gridConfig,
      xAxis: isEvolutionCompact
        ? {
            type: "category",
            data: xAxisData,
            axisLabel: {
              interval: "auto",
              hideOverlap: true,
              color: "#999",
              fontSize: 11,
            },
            axisLine: { lineStyle: { color: "#ddd", width: 1 } },
            splitLine: { show: false },
          }
        : {
            type: "time",
            axisLabel: {
              formatter: (value) => formatAxisLabelEvolution(value),
              hideOverlap: true,
              rotate: shouldRotate ? 45 : 0,
              interval: "auto",
              color: "#999",
              fontSize: 11,
            },
            axisLine: { lineStyle: { color: "#ddd", width: 1 } },
            splitLine: { show: false },
          },
      yAxis: {
        type: "value",
        name: unit || "",
        nameLocation: "end",
        nameGap: 10,
        splitLine: {
          lineStyle: { color: "#f0f0f0", width: 1, type: "solid" },
          show: true,
        },
        axisLine: { lineStyle: { color: "#999", width: 1 } },
        axisLabel: { color: "#999", fontSize: 11 },
      },
      series,
    };

    myChart.setOption(option, true);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      myChart.dispose();
      chartInstance.current = null;
    };
  }, [
    dataChart,
    periodColors,
    unit,
    colorMode,
    viewMode,
    deviceNameByMac,
    hasTotal,
    baseColor,
    heightPx,
  ]);

  useEffect(() => {
    if (!chartRef.current) return;

    const ro = new ResizeObserver(() => {
      if (!chartInstance.current) return;
      if (rafId.current) cancelAnimationFrame(rafId.current);

      rafId.current = requestAnimationFrame(() => {
        if (chartInstance.current) chartInstance.current.resize();
      });
    });

    ro.observe(chartRef.current);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      ro.disconnect();
    };
  }, []);

  // Usar altura pasada o por defecto (responsive)
  const containerHeight = heightPx ? `${heightPx}px` : "500px";

  return (
    <div
      style={{
        width: "100%",
        height: containerHeight,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!hasTotal && (
        <div
          style={{
            display: "flex",
            gap: 0,
            marginBottom: 14,
            flexShrink: 0,
            width: "fit-content",
            padding: "3px",
            backgroundColor: "#f0f2f5",
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
          }}
        >
          <button
            type="button"
            onClick={() => setViewMode("phases")}
            style={{
              padding: "6px 14px",
              borderRadius: "4px",
              border: "none",
              background: viewMode === "phases" ? "#ffffff" : "transparent",
              color: viewMode === "phases" ? "#1976d2" : "#7f8c8d",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: viewMode === "phases" ? 600 : 500,
              transition: "all 0.2s ease",
              boxShadow:
                viewMode === "phases" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
            }}
          >
            Por fases
          </button>

          <button
            type="button"
            onClick={() => setViewMode("total")}
            style={{
              padding: "6px 14px",
              borderRadius: "4px",
              border: "none",
              background: viewMode === "total" ? "#ffffff" : "transparent",
              color: viewMode === "total" ? "#1976d2" : "#7f8c8d",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: viewMode === "total" ? 600 : 500,
              transition: "all 0.2s ease",
              boxShadow:
                viewMode === "total" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
            }}
          >
            Total
          </button>
        </div>
      )}

      <div
        ref={chartRef}
        style={{ width: "100%", flex: 1, minHeight: 0, paddingBottom: "15px" }}
      />
    </div>
  );
};

export default Chart1;
